import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { of } from 'rxjs';
import { tap } from 'rxjs/operators';

export const AuthGuard = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const router = inject(Router);

  // 检查用户是否已登录
  const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';

  if (!isLoggedIn) {
    // 未登录，重定向到登录页面
    router.navigate(['/login']);
    return of(false);
  }

  // 已登录，检查租户类型和路由权限
  const currentTenant = localStorage.getItem('current_tenant');

  if (!currentTenant) {
    // 没有租户信息，重定向到登录页面
    router.navigate(['/login']);
    return of(false);
  }

  try {
    const tenant = JSON.parse(currentTenant);
    const requestedPath = state.url;

    // 根据租户类型检查路由权限
    if (!hasRoutePermission(tenant.type, requestedPath)) {
      // 没有权限访问该路由，重定向到对应的仪表板
      const dashboardPath = tenant.type === 'Official' ? '/dashboard' : '/tenant-dashboard';
      router.navigate([dashboardPath]);
      return of(false);
    }

    return of(true);
  } catch (error) {
    // 租户信息解析失败，重定向到登录页面
    router.navigate(['/login']);
    return of(false);
  }
};

// 检查路由权限的辅助函数
function hasRoutePermission(tenantType: string, requestedPath: string): boolean {
  // 定义不同租户类型的路由权限
  const routePermissions: { [key: string]: string[] } = {
    Official: [
      '/dashboard',
      '/tools',
      '/socketio-connection-audit',
      '/online-users',
      '/payment-result',
      '/tenants',
      '/accounts',
      '/logs',
      '/systemapps',
      '/scheduled-tasks',
      '/official-third-party-apps',
      '/official-assistant-store',
      '/team-certification-audit',
      '/account',
      '/system/config',
      '/reports',
      '/repairs',
      '/versions',
      '/tenant/detail',
      '/tenant/basic-info',
      '/tenant/service-number-management',
      '/tenant/team-management',
      '/tenant/group-management',
      '/tenant/configuration',
      '/tenant/channel-binding',
      '/tenant/application-list',
      '/tenant/customer-list',
      '/tenants/detail',
      '/logs/detail',
      '/themes',
      '/themes/item',
      '/quota-templates',
      '/quota-plans',
      '/pricing-packages',
      '/point-activity-center',
      '/point-audit-center',
      '/liteconsole-login-authorization',
      // 允許官方租戶訪問機器人服務配置頁
      '/robot-service/config',
      '/robot-service/config-flat',
      '/official-robot-management',
      '/personal-broadcast-list',
      '/personal-broadcast-detail',
      '/personal-inbound-reason'
    ],
    Person: [
      '/tenant-dashboard',
      '/payment-result',
      '/quota-view',
      '/personal-tenant',
      '/personal-channel',
      '/personal-service-setting',
      '/personal-robot-setting',
      '/personal-webchat-setting',
      '/personal-inbound-reason',
      '/personal-assistant-management',
      '/personal-broadcast-list',
      '/personal-broadcast-detail',
      '/personal-business-card',
      '/tenant/team-management',
      '/tenant/group-management',
      '/repairs',
      // 允許個人租戶訪問機器人服務配置頁
      '/robot-service/config',
      '/robot-service/config-flat'
    ],
    // Common（通用/普通）租戶：允許進入個人分身設定與機器人服務配置
    Common: [
      '/tenant-dashboard',
      '/payment-result',
      '/tenant/detail',
      '/tenant/basic-info',
      '/tenant/team-management',
      '/tenant/group-management',
      '/tenant/service-number-management',
      '/tenant/configuration',
      '/tenant/third-party-service',
      '/tenant/channel-binding',
      '/tenant/application-list',
      '/tenant/customer-list',
      '/personal-robot-setting',
      '/robot-service/config',
      '/robot-service/config-flat',
      '/personal-inbound-reason',
      '/personal-assistant-management',
      '/personal-broadcast-list',
      '/personal-broadcast-detail',
      '/tenant/authentication'
    ],
    // 其他租户类型的默认权限
    default: [
      '/tenant-dashboard',
      '/payment-result',
      '/tenant/detail',
      '/tenant/basic-info',
      '/tenant/team-management',
      '/tenant/group-management',
      '/tenant/service-number-management',
      '/tenant/configuration',
      '/tenant/third-party-service',
      '/tenant/channel-binding',
      '/tenant/application-list',
      '/tenant/customer-list',
      '/robot-service/config',
      '/robot-service/config-flat'
    ]
  };

  // 获取对应租户类型的路由权限，如果不存在则使用默认权限
  const allowedRoutes = routePermissions[tenantType] || routePermissions['default'];

  // 检查请求的路径是否在允许的路由列表中
  return allowedRoutes.some(route => requestedPath.startsWith(route));
}
